<template>
	<div data-tour="cc-contact-alert-list">
		<LoadingIcon v-if="isLoading" alignment="center" />
		<FallBack
			v-else-if="!isLoading && error"
			iconStyle="fal"
			icon="exclamation-triangle"
			:border="false"
			message="Unable to retrieve alerts. Please try again later."
		/>
		<FallBack
			v-else-if="!isLoading && !error && totalCount === 0"
			iconStyle="far"
			icon="bell"
			:border="false"
			message="No Available Alerts."
		/>
		<div v-else-if="!isEmpty(currentPageAlerts) && !isLoading">
			<div
				class="alerts-container"
				:data-tour="index === 0 ? 'cc-contact-alert' : ''"
				v-for="(alert, index) in currentPageAlerts"
				:key="index"
			>
				<div class="alert-type-container">
					<AlertTypeIcon :alertType="alert.type.desc" />
					<div class="alert-type">
						<span
							role="button"
							aria-label="Alert type"
							class="type"
							@click="
								handleAlertClick(
									STATUS_ACTION_TYPES.TO_DO,
									alert.alertId,
									alert.status.desc,
								)
							"
						>
							{{ alert.type.desc }}
						</span>
						<span class="value">
							{{
								alert.type.desc === 'Birthday'
									? getDateString(
											alert.alertDateTime,
											alert.type.desc,
									  )
									: alert.value
							}}</span
						>
					</div>
				</div>
				<div class="alert-date-time">
					{{ getDateString(alert.alertDateTime) }}
				</div>
				<AlertStatus class="alert-status" :status="alert.status.desc" />
				<QuickActionsMenu
					:data-tour="
						index === 0 ? 'cc-contact-alert-quick-actions' : ''
					"
					:lightButton="true"
					:lightButtonIcon="['far', 'ellipsis-h']"
					:status="alert.status.desc"
					:menus="
						populateSingleQuickActions(
							alert.status.id,
							alert.loan,
							alert.type,
						)
					"
					@viewInEase="openInEase(alert.loan.easeLoanUrl)"
					@sendEmail="handleSendEmail(alert)"
					@addNote="$emit('onAlertNoteClick', alert.alertId)"
					@sendToBlink="$emit('sendToBlinkClick')"
					@markAsToDo="
						handleUpdateAlertStatus(
							STATUS_ACTION_TYPES.TO_DO,
							alert.alertId,
						)
					"
					@markAsDone="
						handleUpdateAlertStatus(
							STATUS_ACTION_TYPES.DONE,
							alert.alertId,
						)
					"
					@markAsDismissed="
						handleUpdateAlertStatus(
							STATUS_ACTION_TYPES.DISMISSED,
							alert.alertId,
						)
					"
				/>
			</div>
			<Pagination
				v-if="totalCount > 10 && !isLoading"
				:totalCount="totalCount"
				:pageNumber="pageNumber"
				:pageSize="pageSize"
				@onPageChange="handlePageNumberChange"
				@onPageSizeChange="handlePageSizeChange"
			/>
		</div>
	</div>
</template>

<script>
import { isEmpty } from 'lodash';
import { mapActions, mapState } from 'vuex';

import AlertStatus from '../AlertStatus.vue';
import AlertTypeIcon from '../AlertTypeIcon.vue';
import FallBack from '../../../components/global/FallBack.vue';
import LoadingIcon from '../../../components/global/LoadingIcon.vue';
import Pagination from '../../../components/global/Pagination.vue';
import QuickActionsMenu from '../QuickActionsMenu.vue';
import { getAlertQuickActions, openInEase } from '../helpers';
import { STATUS_ACTION_TYPES } from '../constants';
import { CAMPAIGN_ID } from '../../../constants/brand-builder';
import alertsService from '@/services/contacts/alerts-service.js';

export default {
	name: 'ContactDetailsAlertsList',
	props: {
		contactId: Number,
		leadId: Number,
		alertId: Number,
		blinkDisabled: Boolean,
		loggedInUserId: Number,
	},
	computed: {
		...mapState({
			leads: (state) => state.ccContacts.leads.listWithDetails,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
		}),
	},
	components: {
		AlertStatus,
		AlertTypeIcon,
		FallBack,
		LoadingIcon,
		Pagination,
		QuickActionsMenu,
	},
	data() {
		return {
			alerts: [],
			pageNumber: 1,
			pageSize: 10,
			totalCount: null,
			isLoading: false,
			error: null,
			currentPageAlerts: [],
			slicedAlerts: [],
			STATUS_ACTION_TYPES,
		};
	},
	methods: {
		...mapActions(['getLeadAlerts']),
		/**
		 * when we use a function in the template that is imported from outside
		 * the component it needs to be included in the methods object
		 */
		// deepcode ignore VueIncorrectMethodType: see comment above
		isEmpty,
		openInEase,
		async getAlerts(refreshList = false) {
			// eslint-disable-next-line no-shadow
			const lead = this.leads.find((lead) => lead.leadId === this.leadId);
			if (
				lead &&
				Object.prototype.hasOwnProperty.call(lead, 'alerts') &&
				!refreshList
			) {
				this.alerts = lead.alerts;
				this.totalCount = lead.alertsTotalCount;
				this.getCurrentPageAlerts();
			} else {
				try {
					this.isLoading = true;
					await this.getLeadAlerts({
						leadId: this.leadId,
						refreshList,
					});
					this.isLoading = false;
				} catch (error) {
					this.error = error;
					this.isLoading = false;
				}
			}
		},
		getCurrentPageAlerts() {
			this.slicedAlerts = this.alerts.slice(
				(this.pageNumber - 1) * this.pageSize,
				this.pageNumber * this.pageSize,
			);
			if (!isEmpty(this.slicedAlerts)) {
				this.currentPageAlerts = [...this.slicedAlerts];
			}
		},
		handlePageNumberChange(page) {
			this.pageNumber = page;
			this.getCurrentPageAlerts();
		},
		handlePageSizeChange(size) {
			this.pageSize = size;
			this.pageNumber = 1;
			this.getCurrentPageAlerts();
		},
		getDateString(date, alertType = '') {
			if (alertType === 'Birthday') {
				return new Date(date).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
				});
			} else {
				return new Date(date).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric',
				});
			}
		},
		async handleUpdateAlertStatus(statusId, alertId) {
			const payload = {
				alertIds: [alertId],
				statusId,
			};
			try {
				await alertsService.updateAlertStatus(payload);
				this.$emit('statusUpdated');
				await this.getAlerts(true);
			} catch (error) {
				this.error = error;
			}
		},
		async handleAlertClick(statusId, alertId, status) {
			// This changes the status from new to to-do when the user opens the alert
			if (status === 'New' && !this.mainProfileIsInternalUser) {
				await this.handleUpdateAlertStatus(statusId, alertId);
			}
			this.$emit('onAlertClick', alertId);
		},
		handleSendEmail(alert) {
			const campaignId = CAMPAIGN_ID[alert.type.desc];
			this.$router.push({
				path: `/campaign-editor-workspace/${campaignId}/media`,
				query: {
					alertId: alert.alertId,
					borrowerFirstName: alert.lead.firstName,
					borrowerLastName: alert.lead.lastName,
					email: alert.lead.email,
					leadId: alert.lead.leadId,
					alertType: alert.type.desc,
				},
			});
		},
		populateSingleQuickActions(statusId, loanInfo, alertType) {
			let easeUrl = '';
			let isUwmLoan = false;
			let isLoanAlert = false;
			if (loanInfo) {
				isLoanAlert = true;
				easeUrl = loanInfo.easeLoanUrl;
				isUwmLoan = loanInfo.uwmInd;
			}

			return getAlertQuickActions(
				false,
				this.contactId,
				statusId,
				this.mainProfileIsInternalUser,
				this.isImpersonated,
				this.loggedInUserId,
				isLoanAlert,
				easeUrl,
				isUwmLoan,
				alertType,
			);
		},
	},
	async created() {
		await this.getAlerts(true);
	},
	watch: {
		async leads() {
			// eslint-disable-next-line no-shadow
			const lead = this.leads.find((lead) => lead.leadId === this.leadId);
			this.alerts = lead.alerts;
			this.totalCount = lead.alertsTotalCount;
			this.getCurrentPageAlerts();
		},
	},
};
</script>

<style lang="scss" scoped>
.alerts-container {
	display: grid;
	grid-template-columns: 45% 24% 25% auto;
	@include rem('padding-left', 20px);
	@include rem('padding-right', 15px);
	@include rem('height', 70px);
	align-items: center;
	border-bottom: 1px solid $border-color-light;

	@media only screen and (max-width: 600px) {
		grid-template-columns: 86% auto;

		.alert-date-time,
		.alert-status {
			display: none;
		}
	}

	.alert-type-container {
		display: flex;

		.alert-type {
			display: flex;
			flex-direction: column;
			@include rem('margin-left', 15px);

			.type {
				color: $brand-blue;
				font-weight: 500;
				cursor: pointer;
			}

			.value {
				@include rem('margin-top', 3px);
			}
		}
	}
}
.spinner {
	@include rem('padding', 25px);
	display: flex;
	justify-content: center;
	align-items: center;
}
</style>
